import React, { useState, useEffect, useRef } from 'react';
import SharedHeaderContractor from './SharedHeaderContractor';
import { useHeadlessAuth, silentTicketSignIn } from '../auth/headlessSignup';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';

interface ContractorSignupProps {
  onNavigate: (screen: string) => void;
}

// CB:BEGIN contractor-signup-component
const ContractorSignup: React.FC<ContractorSignupProps> = ({ onNavigate }) => {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { silentSignIn } = useHeadlessAuth();

  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<{[k:string]: string}>({});
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeSms, setAgreeSms] = useState(true);
  const [complianceVersions, setComplianceVersions] = useState<{ currentTermsVersion: string; currentPrivacyVersion: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailTooltip, setEmailTooltip] = useState(false);
  const [phoneTooltip, setPhoneTooltip] = useState(false);

  const businessAddressRef = useRef<HTMLInputElement>(null);

  // Clear stale role cache so switching roles works cleanly
  useEffect(() => { try { localStorage.removeItem('cb.usertype'); } catch {} }, []);

  // Google Places autocomplete for Business Address
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY;
    if (!apiKey) return;

    const attachAutocomplete = () => {
      const inputEl = businessAddressRef.current;
      if (!inputEl || !(window as any).google?.maps?.places) return;
      if ((inputEl as any).__gacAttached) return;
      (inputEl as any).__gacAttached = true;

      const ac = new (window as any).google.maps.places.Autocomplete(inputEl, {
        types: ['address'],
        fields: ['formatted_address'],
        componentRestrictions: { country: 'us' },
      });

      // Ensure dropdown renders above all UI and suppress Google branding
      if (!document.getElementById('pac-style-overrides')) {
        const style = document.createElement('style');
        style.id = 'pac-style-overrides';
        style.textContent = `.pac-container { z-index: 9999 !important; } .pac-icon, .pac-icon-marker { display: none !important; } .pac-logo::after { display: none !important; } .pac-item { padding: 6px 12px; }`;
        document.head.appendChild(style);
      }

      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place?.formatted_address) {
          if (businessAddressRef.current) businessAddressRef.current.value = place.formatted_address;
          setBusinessAddress(place.formatted_address);
        }
      });
    };

    const scriptId = 'google-places-script';
    if ((window as any).google?.maps?.places) {
      attachAutocomplete();
    } else if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=__cbPlacesReady`;
      script.async = true;
      (window as any).__cbPlacesReady = () => { attachAutocomplete(); delete (window as any).__cbPlacesReady; };
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if ((window as any).google?.maps?.places) { clearInterval(poll); attachAutocomplete(); }
      }, 100);
      setTimeout(() => clearInterval(poll), 10000);
    }

    return () => { if (businessAddressRef.current) delete (businessAddressRef.current as any).__gacAttached; };
  }, []);
  // CB:BEGIN client-trust-2fa-state – Client Trust: second factor (email code) when sign-in requires it
  const [showSecondFactorCode, setShowSecondFactorCode] = useState(false);
  const [secondFactorSignIn, setSecondFactorSignIn] = useState<any>(null);
  const [secondFactorCode, setSecondFactorCode] = useState('');
  const [pendingClerkId, setPendingClerkId] = useState<string | null>(null);
  // CB:END client-trust-2fa-state

  const apiBase = getApiBaseUrl();

  // CB:BEGIN compliance-tracking - fetch current ToS/Privacy versions on mount
  React.useEffect(() => {
    fetch(`${apiBase.replace(/\/$/, '')}/api/compliance/versions`)
      .then((r) => r.json())
      .then((d) => setComplianceVersions(d))
      .catch(() => setComplianceVersions({ currentTermsVersion: '1.0', currentPrivacyVersion: '1.0' }));
  }, [apiBase]);
  // CB:END compliance-tracking

  const validate = () => {
    if (!businessName.trim()) return 'Business Name is required';
    if (!businessAddress.trim()) return 'Business Address is required';
    if (!email.includes('@')) return 'Valid email is required';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return 'Valid phone is required';
    if (!agreeTerms) return 'You must agree to the Terms & Conditions';
    if (!agreePrivacy) return 'You must agree to the Privacy Policy';
    if (!isSignedIn) {
      const okLen = password.length >= 8;
      const hasLetter = /[A-Za-z]/.test(password);
      const hasNumber = /\d/.test(password);
      // eslint-disable-next-line no-useless-escape
      const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(password);
      if (!okLen) return 'Password must be at least 8 characters';
      if (!hasLetter) return 'Password must contain at least one letter';
      if (!hasNumber) return 'Password must contain at least one number';
      if (!hasSpecialChar) return 'Password must contain at least one special character (!@#$%^&*)';
      if (password !== confirm) return 'Passwords do not match';
    }
    return null;
  };

  function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === 'password') setPassword(value); else setConfirm(value);
    const pwd = name === 'password' ? value : password;
    const conf = name === 'passwordConfirm' ? value : confirm;
    const next: {[k:string]: string} = {};
    if (pwd && conf && pwd !== conf) next.passwordConfirm = 'Passwords do not match';
    setPasswordErrors(prev => ({ ...prev, ...next, ...(next.passwordConfirm ? {} : { passwordConfirm: '' }) }));
  }

  async function handleSubmit() {
    if (busy) return;
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true);
    try {
      let clerkId = user?.id || '';

      // 1) Create Clerk account if not signed in
      if (!isSignedIn) {
        const resp = await fetch(`${apiBase.replace(/\/$/, '')}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            firstName: businessName.trim() || email.split('@')[0] || 'Contractor',
            lastName: '',
            phone,
            role: 'CONTRACTOR', // CB:BEGIN contractor-registration
            textOptIn: agreeSms // CB:END contractor-registration
          })
        });
        if (!resp.ok) {
          let body: any = null;
          try { body = await resp.json(); } catch {}
          const msg = body?.message || body?.error || 'Registration failed. Please try again.';
          throw Object.assign(new Error(msg), { code: body?.error || 'register_failed' });
        }
        const data = await resp.json();
        clerkId = data.userId;
        // Try ticket-based silent sign-in first (preferred)
        let signed = await silentTicketSignIn(email, { firstName: businessName.trim() || email.split('@')[0] || 'Contractor', lastName: '', phone });
        if (!signed) {
          // Fallback: direct password sign-in via Clerk frontend SDK
          try {
            const clerk: any = (window as any).Clerk;
            if (clerk) {
              await clerk.load();
              const si = await clerk.client.signIn.create({ 
                identifier: email, 
                password 
              });
              if (si?.status === 'complete' && si?.createdSessionId) {
                await clerk.setActive({ session: si.createdSessionId });
                signed = true;
              } else if (si?.status === 'needs_second_factor') {
                // CB:BEGIN client-trust-contractor-2fa – show email code step
                const factors = si?.supportedSecondFactors || [];
                const emailCodeFactor = factors.find((f: { strategy: string }) => f.strategy === 'email_code');
                if (emailCodeFactor) {
                  await si.prepareSecondFactor({ strategy: 'email_code', emailAddressId: emailCodeFactor.emailAddressId });
                  setPendingClerkId(clerkId);
                  setSecondFactorSignIn(si);
                  setShowSecondFactorCode(true);
                  setBusy(false);
                  return;
                }
                // CB:END client-trust-contractor-2fa
              }
            }
          } catch (e) {
            console.warn('Password sign-in fallback failed:', e);
          }
        }
        // Cache role immediately to avoid role-gate redirect during first render after sign-in
        try { localStorage.setItem('cb.usertype', 'contractor'); } catch {}
      }

      // 2) Ensure DB user exists as CONTRACTOR (signup creates User + Contractor)
      await fetch(`${apiBase.replace(/\/$/, '')}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clerkId,
          email,
          account_type: 'CONTRACTOR',
        }),
      }).catch(() => {});

      // 3) Call contractor onboard (company name, address, geocode)
      const onboardResp = await fetch(`${apiBase.replace(/\/$/, '')}/api/contractors/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerkId, email, companyName: businessName.trim(), dba: '', address: businessAddress.trim(), phone: phone.trim() || undefined })
      });
      if (!onboardResp.ok) {
        console.warn('contractors/onboard failed:', onboardResp.status);
      } else {
        const onboardData = await onboardResp.json();
        
        // Cache the usertype immediately to prevent role gate issues
        if (onboardData.usertype) {
          localStorage.setItem('cb.usertype', onboardData.usertype);
          console.log('✅ Cached contractor usertype from onboard:', onboardData.usertype);
          console.log('🔍 localStorage after setting:', localStorage.getItem('cb.usertype'));
        } else {
          console.warn('⚠️ No usertype in onboard response:', onboardData);
        }
      }

      // 3a) Record ToS/Privacy acceptance and independent contractor acknowledgment (auditable)
      const token = typeof getToken === 'function' ? await getToken() : null;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const termsVer = complianceVersions?.currentTermsVersion ?? '1.0';
      const privacyVer = complianceVersions?.currentPrivacyVersion ?? '1.0';
      await fetch(`${apiBase.replace(/\/$/, '')}/api/compliance/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ termsVersion: termsVer, privacyVersion: privacyVer }),
      }).catch(() => {});

      // Note: Clerk metadata usertype sync and demo notification seeding are handled
      // server-side via the middleware/auth.ts sync-clerk-role-to-db flow.

      // CB:BEGIN invite-token-redirect
      // Check if there's an invite token in URL params
      const urlParams = new URLSearchParams(window.location.search);
      const inviteToken = urlParams.get('inviteToken');
      
      if (inviteToken) {
        // Redirect to invite preview page with auto=1 flag to trigger auto-redirect after auth
        window.location.href = `/invite/${inviteToken}?auto=1`;
        return;
      }
      // CB:END invite-token-redirect
      
      // Navigate to contractor dashboard
        onNavigate('contractor-dashboard');
    } catch (e: any) {
      const CODE_MESSAGES: Record<string, string> = {
        form_password_pwned:        'This password was found in a data breach. Please choose a different, unique password.',
        form_identifier_exists:     'An account with this email already exists. Try signing in instead.',
        form_password_too_short:    'Password must be at least 8 characters.',
        form_password_no_uppercase: 'Password must contain at least one uppercase letter.',
        form_password_no_lowercase: 'Password must contain at least one lowercase letter.',
        form_password_no_digit:     'Password must contain at least one number.',
        form_password_no_special_char: 'Password must contain at least one special character.',
        form_param_format_invalid:  'One or more fields are invalid. Please check your email and password.',
      };
      const friendly = CODE_MESSAGES[(e as any)?.code] ?? e?.message ?? 'Signup failed';
      setError(friendly);
    } finally {
      setBusy(false);
    }
  }

  // CB:BEGIN client-trust-contractor-2fa-submit – complete sign-in after email code and run post-sign-in steps
  async function handleSecondFactorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!secondFactorSignIn || !pendingClerkId || busy) return;
    setError(null);
    setBusy(true);
    try {
      const attempt = await secondFactorSignIn.attemptSecondFactor({ strategy: 'email_code', code: secondFactorCode });
      if (attempt?.status !== 'complete' || !attempt?.createdSessionId) {
        setError('Invalid or expired code. Please try again.');
        return;
      }
      const clerk: any = (window as any).Clerk;
      if (clerk) await clerk.setActive({ session: attempt.createdSessionId });
      try { localStorage.setItem('cb.usertype', 'contractor'); } catch {}
      const clerkId = pendingClerkId;
      setShowSecondFactorCode(false);
      setSecondFactorSignIn(null);
      setPendingClerkId(null);
      setSecondFactorCode('');

      await fetch(`${apiBase.replace(/\/$/, '')}/api/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: clerkId,
          email,
          account_type: 'CONTRACTOR',
        }),
      }).catch(() => {});

      const onboardResp = await fetch(`${apiBase.replace(/\/$/, '')}/api/contractors/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerkId, email, companyName: businessName.trim(), dba: '', address: businessAddress.trim(), phone: phone.trim() || undefined })
      });
      if (onboardResp.ok) {
        const onboardData = await onboardResp.json();
        if (onboardData.usertype) localStorage.setItem('cb.usertype', onboardData.usertype);
      }

      const token = typeof getToken === 'function' ? await getToken() : null;
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${apiBase.replace(/\/$/, '')}/api/compliance/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ termsVersion: complianceVersions?.currentTermsVersion ?? '1.0', privacyVersion: complianceVersions?.currentPrivacyVersion ?? '1.0' }),
      }).catch(() => {});

      const urlParams = new URLSearchParams(window.location.search);
      const inviteToken = urlParams.get('inviteToken');
      if (inviteToken) {
        window.location.href = `/invite/${inviteToken}?auto=1`;
        return;
      }
      onNavigate('contractor-dashboard');
    } catch (err: any) {
      setError(err?.message ?? 'Verification failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  // CB:END client-trust-contractor-2fa-submit

  // CB:BEGIN client-trust-contractor-2fa-ui – show email code step when Client Trust requires second factor
  if (showSecondFactorCode) {
    return (
      <div className="container">
        <h2 style={{ marginBottom: 16 }}>Verify your email</h2>
        <p style={{ color: '#666', marginBottom: 16 }}>We sent a verification code to your email. Enter it below.</p>
        <form onSubmit={handleSecondFactorSubmit}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Verification code"
            value={secondFactorCode}
            onChange={(e) => setSecondFactorCode(e.target.value)}
            style={{ width: '100%', maxWidth: 280, padding: 10, marginBottom: 12, border: '1px solid #ccc', borderRadius: 6 }}
          />
          {error && <p style={{ color: '#c00', marginBottom: 8 }}>{error}</p>}
          <button type="submit" disabled={busy} style={{ padding: '10px 20px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6 }}>Verify and continue</button>
        </form>
      </div>
    );
  }
  // CB:END client-trust-contractor-2fa-ui

  return (
    <div className="container">
      <style>{`
        #contractor-terms-checkbox, #contractor-privacy-checkbox, #contractor-sms-checkbox {
          width: 16px !important;
          height: 16px !important;
          min-width: 16px !important;
          min-height: 16px !important;
          max-width: 16px !important;
          max-height: 16px !important;
          transform: scale(0.75) !important;
          transform-origin: left center !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          border: 1px solid #d1d5db !important;
          border-radius: 2px !important;
          background-color: white !important;
          cursor: pointer !important;
          margin: 0 !important;
        }
        #contractor-terms-checkbox:checked, #contractor-privacy-checkbox:checked, #contractor-sms-checkbox:checked {
          background-color: white !important;
          border-color: #d1d5db !important;
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000000' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M6 10l3 3 6-6'/%3e%3c/svg%3e") !important;
          background-size: 100% 100% !important;
          background-position: center !important;
          background-repeat: no-repeat !important;
          box-shadow: none !important;
        }
      `}</style>
      <SharedHeaderContractor onNavigate={onNavigate} />
      <div className="content">
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Join as a Contractor</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label htmlFor="businessName" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Business Name *</label>
              <input
                id="businessName"
                className="form-control"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="CoreBids Roofer LLC"
              />
            </div>
            <div>
              <label htmlFor="businessAddress" style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Business Address *</label>
              <input
                id="businessAddress"
                ref={businessAddressRef}
                className="form-control"
                value={businessAddress}
                onChange={(e) => setBusinessAddress(e.target.value)}
                placeholder="Start typing your address…"
                autoComplete="new-password"
              />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}>
                Start typing — select your address from the Google suggestions that appear.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label htmlFor="email" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>
                  Email *
                  <span
                    style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}
                    onMouseEnter={() => setEmailTooltip(true)}
                    onMouseLeave={() => setEmailTooltip(false)}
                  >
                    <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#e5e7eb', color: '#6b7280', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>i</span>
                    {emailTooltip && (
                      <span style={{ position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: '#1f2937', color: '#fff', fontSize: 12, fontWeight: 400, padding: '8px 10px', borderRadius: 6, width: 240, zIndex: 100, lineHeight: 1.4, whiteSpace: 'normal', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        This email will be your primary contact address for all communication with CoreBids — including project updates, bid notifications, and urgent issue alerts. Use an address you check regularly.
                        <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderWidth: 5, borderStyle: 'solid', borderColor: '#1f2937 transparent transparent transparent' }} />
                      </span>
                    )}
                  </span>
                </label>
                <input id="email" className="form-control" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" />
              </div>
              <div>
                <label htmlFor="phone" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>
                  Phone Number *
                  <span
                    style={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }}
                    onMouseEnter={() => setPhoneTooltip(true)}
                    onMouseLeave={() => setPhoneTooltip(false)}
                  >
                    <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#e5e7eb', color: '#6b7280', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>i</span>
                    {phoneTooltip && (
                      <span style={{ position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)', background: '#1f2937', color: '#fff', fontSize: 12, fontWeight: 400, padding: '8px 10px', borderRadius: 6, width: 240, zIndex: 100, lineHeight: 1.4, whiteSpace: 'normal', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        This phone number is your direct line for CoreBids. We may contact you for time-sensitive project issues, scheduling confirmations, or payment questions. Must be reachable during business hours.
                        <span style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', borderWidth: 5, borderStyle: 'solid', borderColor: '#1f2937 transparent transparent transparent' }} />
                      </span>
                    )}
                  </span>
                </label>
                <input id="phone" className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
              </div>
            </div>
            {!isSignedIn && (
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: '14px' }}>Password *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    required
                    name="password"
                    value={password}
                    onChange={handlePasswordChange}
                    className="form-control"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    style={{ padding: '8px 12px', fontSize: '14px', flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    className="btn btn-secondary"
                    style={{ padding: '8px 12px', fontSize: '12px', minWidth: 60 }}
                  >
                    {showPw ? 'Hide' : 'Show'}
                  </button>
                </div>
                <label style={{ display: 'block', marginBottom: 4, marginTop: 8, fontWeight: 600, fontSize: '14px' }}>Confirm Password *</label>
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  name="passwordConfirm"
                  value={confirm}
                  onChange={handlePasswordChange}
                  className="form-control"
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  style={{ padding: '8px 12px', fontSize: '14px' }}
                />
                {passwordErrors.passwordConfirm && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>{passwordErrors.passwordConfirm}</p>}
                {pwError && <p style={{ color: '#dc2626', fontSize: 12, marginTop: 2 }}>{pwError}</p>}
              </div>
            )}
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: '13px', fontWeight: 400 }}>
                <input 
                  id="contractor-terms-checkbox"
                  type="checkbox" 
                  checked={agreeTerms} 
                  onChange={(e) => setAgreeTerms(e.target.checked)} 
                  style={{ 
                    width: '16px', 
                    height: '16px', 
                    minWidth: '16px',
                    minHeight: '16px',
                    accentColor: '#2563eb', 
                    cursor: 'pointer', 
                    margin: 0,
                    transform: 'scale(0.75)',
                    transformOrigin: 'left center',
                    flexShrink: 0
                  }} 
                />
                <span style={{ color: '#1f2937' }}>I Agree to the <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> *</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, fontSize: '13px', fontWeight: 400 }}>
                <input
                  id="contractor-privacy-checkbox"
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    minWidth: '16px',
                    minHeight: '16px',
                    cursor: 'pointer',
                    margin: 0,
                    transform: 'scale(0.75)',
                    transformOrigin: 'left center',
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: '#1f2937' }}>I Agree to the <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a> *</span>
              </label>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '13px', fontWeight: 400 }}>
                <input
                  id="contractor-sms-checkbox"
                  type="checkbox"
                  checked={agreeSms}
                  onChange={(e) => setAgreeSms(e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    minWidth: '16px',
                    minHeight: '16px',
                    cursor: 'pointer',
                    margin: 0,
                    transform: 'scale(0.75)',
                    transformOrigin: 'left center',
                    flexShrink: 0,
                  }}
                />
                <span style={{ color: '#1f2937' }}>I Agree to receive text messages</span>
              </label>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, lineHeight: 1.3 }}>
                By entering your number & checking I agree to recieve text messages, you agree to receive automated texts from CoreBids, Inc. at 877-570-7485 for customer care, project updates, and support. Msg frequency varies. Msg & data rates may apply. Text STOP to cancel, HELP for help. See <a href="/terms" style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href="#" onClick={(e) => { e.preventDefault(); onNavigate('privacy-policy'); }} style={{ color: '#2563eb', textDecoration: 'underline' }} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </div>
            </div>

            {error && <div style={{ color: '#dc2626', fontSize: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={busy}>
                {busy ? 'Working…' : 'Start Bidding Now!'}
              </button>
            </div>
            
            {/* CB:BEGIN contractor-feedback-button */}
            {/* Feedback button removed */}
            {/* CB:END contractor-feedback-button */}
          </div>
        </div>
      </div>
    </div>
  );
};
// CB:END contractor-signup-component

export default ContractorSignup;


